/* eslint-disable max-lines-per-function */
import { useState } from "react";

interface ApprovalDialogProps {
  readonly isOpen: boolean;
  readonly loading: boolean;
  readonly submissionTitle: string;
  readonly hasSubtitles?: boolean;
  readonly onConfirm: (notes: string, publishImmediately: boolean, transferSubtitles: boolean) => void;
  readonly onCancel: () => void;
}

function DialogHeader({ submissionTitle }: { readonly submissionTitle: string }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className="w-10 h-10 bg-green-600/20 rounded-full flex items-center justify-center flex-shrink-0">
        <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div className="flex-1">
        <h3 className="text-xl font-bold text-white mb-1">Approve Submission</h3>
        <p className="text-sm text-neutral-400">
          You are about to approve <span className="text-white font-medium">"{submissionTitle}"</span>
        </p>
      </div>
    </div>
  );
}

/**
 * ApprovalDialog - Modal for approving submissions
 * Allows admin to add optional notes and choose to publish immediately
 * Phase 1: UI with mock workflow
 * Phase 2: Real API integration in Task 6
 */
export function ApprovalDialog({
  isOpen,
  loading,
  submissionTitle,
  hasSubtitles = false,
  onConfirm,
  onCancel,
}: ApprovalDialogProps) {
  const [notes, setNotes] = useState("");
  const [publishImmediately, setPublishImmediately] = useState(false);
  const [transferSubtitles, setTransferSubtitles] = useState(true);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(notes, publishImmediately, transferSubtitles);
    setNotes("");
    setPublishImmediately(false);
    setTransferSubtitles(true);
  };

  const handleCancel = () => {
    onCancel();
    setNotes("");
    setPublishImmediately(false);
    setTransferSubtitles(true);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 max-w-lg w-full">
        <DialogHeader submissionTitle={submissionTitle} />

        {/* Description */}
        <div className="bg-green-600/10 border border-green-600/30 rounded-lg p-3 mb-4">
          <p className="text-sm text-green-400">
            The content provider will be notified that their submission has been approved.
          </p>
        </div>

        {/* Admin Notes */}
        <div className="mb-4">
          <label htmlFor="approval-notes" className="text-sm text-neutral-400 block mb-2">
            Admin Notes (optional)
          </label>
          <textarea
            id="approval-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add feedback or notes for the provider..."
            rows={3}
            disabled={loading}
            className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          />
          <p className="text-xs text-neutral-500 mt-1">These notes will be included in the notification email</p>
        </div>

        {/* Publish Immediately Toggle */}
        <div className="mb-6">
          <label htmlFor="publish-immediately" className="flex items-start gap-3 cursor-pointer">
            <div className="flex items-center h-5">
              <input
                id="publish-immediately"
                type="checkbox"
                checked={publishImmediately}
                onChange={(e) => setPublishImmediately(e.target.checked)}
                disabled={loading}
                aria-label="Publish immediately checkbox"
                className="w-4 h-4 bg-neutral-800 border-neutral-700 rounded text-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div className="flex-1">
              <span className="text-sm font-medium text-white block">Publish Immediately</span>
              <span className="text-xs text-neutral-400 block mt-0.5">
                Make this content available on the platform right away
              </span>
            </div>
          </label>
        </div>

        {/* Transfer Subtitles Toggle */}
        {hasSubtitles && (
          <div className="mb-6">
            <label htmlFor="transfer-subtitles" className="flex items-start gap-3 cursor-pointer">
              <div className="flex items-center h-5">
                <input
                  id="transfer-subtitles"
                  type="checkbox"
                  checked={transferSubtitles}
                  onChange={(e) => setTransferSubtitles(e.target.checked)}
                  disabled={loading}
                  aria-label="Transfer subtitles checkbox"
                  className="w-4 h-4 bg-neutral-800 border-neutral-700 rounded text-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex-1">
                <span className="text-sm font-medium text-white block">Transfer Subtitles to Stream</span>
                <span className="text-xs text-neutral-400 block mt-0.5">
                  Upload provider-submitted subtitle files to Cloudflare Stream
                </span>
              </div>
            </label>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Approving..." : "Approve Submission"}
          </button>
        </div>
      </div>
    </div>
  );
}
